import {defineStore} from 'pinia'
import {Ref, ref} from 'vue'
import {User} from "@/graphql/generated/graphql";
import {fetchMyUser} from "@/helper/auth";

export const useAuthStore = defineStore('auth', () => {
    
    const jwt_string: Ref<string|undefined> = ref(undefined)
    const roles = ref([] as string[])
    const currentUser: Ref<User | undefined> = ref(undefined)
    const impersonatingUser: Ref<User | undefined> = ref(undefined)
    const firebaseCloudMessagingRegistrationToken: Ref<string|undefined> = ref(undefined);
    const needsPromptForPushNotificationsPermission = ref(false)
    const didRequestForPushNotificationsPermission = ref(false)

    const purgeAuth = () => {
        resetImpersonation()
        setRoles(undefined)
        setToken(undefined)
    }

    const setRoles = (value: string[] | undefined): void => {
        roles.value = value || []
    }
    const isUserRole = (role: 'candidate'|'employee'|'consultant'): boolean => {
        switch (role) {
            case 'candidate': return roles.value.includes('ROLE_CANDIDATE')
            case 'employee': return roles.value.includes('ROLE_EMPLOYEE')
            case 'consultant': return roles.value.includes('ROLE_CONSULTANT')
        }
    }

    const getCurrentUser = async () : Promise<User | undefined> => {
        if (!currentUser.value) {
            await refreshCurrentUser()
        }
        return currentUser.value
    }
    const refreshCurrentUser = async (): Promise<User | undefined> => {
        currentUser.value = await fetchMyUser({useImpersonation: false})
        if (currentUser.value) {
            setRoles(currentUser.value.roles);
        }
        return currentUser.value
    }

    const getFCMToken = () => firebaseCloudMessagingRegistrationToken.value
    const setFCMToken = (token: string | undefined): void => {
        firebaseCloudMessagingRegistrationToken.value = token
    }
    
    const getToken = () => jwt_string.value
    const hasToken = () => getToken() !== undefined
    
    const setToken = (newToken: string | undefined) => {        
        if (jwt_string.value !== newToken) {
            // const oldToken = jwt_string.value
            jwt_string.value = newToken
            // emitter.emit('auth:token:update', {newToken,  oldToken})
        }
    }
    
    const canImpersonate = () => roles.value.includes('ROLE_ALLOWED_TO_SWITCH')
    const isImpersonating = () => isUserRole('consultant') && !!impersonatingUser.value
    const getImpersonationUsername = () => impersonatingUser.value?.userName

    const setImpersonation = (user: User): void => {
        impersonatingUser.value = user
    }
    const resetImpersonation = (): void => {
        impersonatingUser.value = undefined
    }
    const getImpersonation = (): User | undefined => {
        return impersonatingUser.value
    }

    return {
        jwt_string,
        roles,
        impersonatingUser,
        currentUser,
        firebaseCloudMessagingRegistrationToken,     

        purgeAuth,

        getToken,
        hasToken,
        setToken,
        setRoles,
        refreshCurrentUser,
        getCurrentUser,
        isUserRole,
        canImpersonate,
        setImpersonation,
        resetImpersonation,
        getImpersonation,
        getImpersonationUsername,
        isImpersonating,
        setFCMToken,
        getFCMToken,
        needsPromptForPushNotificationsPermission,
        didRequestForPushNotificationsPermission
    }
}, {
    persist: true,
})
